/**
 * Webストレージサービス
 * ブラウザのlocalStorageを非同期サービス契約で安全に利用します。
 * localStorageを直接参照してよいのはこのファイルだけです。
 */

import { logWarn } from "../utils/logger";

/** 保存先。localStorageを参照できない端末ではメモリ（ページを開いている間だけ）に保存する */
type StorageBackend = "local" | "memory";

/** localStorageへ書き込めるかを確かめるキー */
const STORAGE_PROBE_KEY = "@ikomasai/storage_probe";

/** localStorageを使えない環境向けのメモリ保存先 */
const memoryStorage = new Map<string, string>();

/** 初回の利用時に1回だけ決める保存先 */
let storageBackend: StorageBackend | null = null;

/** 初回の確認時に1回だけ決める、再読み込み後も残せるか */
let canPersistStorage: boolean | null = null;

/**
 * localStorageを1回だけ読んで、保存先を決める
 * Cookieを全ブロックした端末ではlocalStorageの参照自体がSecurityErrorになるため、メモリへ切り替える。
 * 書き込みだけ失敗する端末（容量切れ）では、保存済みのログイン状態を読めるようlocalStorageのままにする。
 * @returns {StorageBackend} 保存先
 */
const resolveStorageBackend = (): StorageBackend => {
  if (storageBackend) {
    return storageBackend;
  }

  try {
    window.localStorage.getItem(STORAGE_PROBE_KEY);
    storageBackend = "local";
  } catch (error) {
    storageBackend = "memory";
    logWarn("この端末ではlocalStorageを参照できないため、ページを開いている間だけ保存します。", {
      errorName: error instanceof Error ? error.name : String(error),
    });
  }
  return storageBackend;
};

/**
 * ログイン状態や設定を端末に保存し、再読み込み後も残せるか判定する（初回に1回だけ試し書きする）
 * @returns {boolean} localStorageへ書き込める場合true
 */
export const isPersistentStorageAvailable = (): boolean => {
  if (canPersistStorage !== null) {
    return canPersistStorage;
  }
  if (resolveStorageBackend() !== "local") {
    canPersistStorage = false;
    return canPersistStorage;
  }

  try {
    window.localStorage.setItem(STORAGE_PROBE_KEY, "1");
    window.localStorage.removeItem(STORAGE_PROBE_KEY);
    canPersistStorage = true;
  } catch (error) {
    canPersistStorage = false;
    logWarn("この端末ではlocalStorageへ書き込めないため、ログイン状態や設定を保存できません。", {
      errorName: error instanceof Error ? error.name : String(error),
    });
  }
  return canPersistStorage;
};

/** WebストレージAPI */
const webStorage = {
  /**
   * 値を保存する
   * @param {string} key - 保存キー
   * @param {string} value - 保存値
   * @returns {Promise<void>}
   */
  async setItem(key: string, value: string) {
    if (resolveStorageBackend() === "local") {
      window.localStorage.setItem(key, value);
      return;
    }
    memoryStorage.set(key, value);
  },

  /**
   * 値を取得する
   * @param {string} key - 保存キー
   * @returns {Promise<string | null>} 保存値
   */
  async getItem(key: string) {
    if (resolveStorageBackend() === "local") {
      return window.localStorage.getItem(key);
    }
    return memoryStorage.get(key) ?? null;
  },

  /**
   * 値を削除する
   * @param {string} key - 保存キー
   * @returns {Promise<void>}
   */
  async removeItem(key: string) {
    if (resolveStorageBackend() === "local") {
      window.localStorage.removeItem(key);
      return;
    }
    memoryStorage.delete(key);
  },

  /**
   * 全値を削除する
   * @returns {Promise<void>}
   */
  async clear() {
    if (resolveStorageBackend() === "local") {
      window.localStorage.clear();
    }
    memoryStorage.clear();
  },
};

export default webStorage;
